import React from "react";
import moment from "moment";
import _ from "lodash";
import {ConfigurationsType} from "../types/configurations";
import {hasBeenFilled} from "./dynamicFormHelper";
import {
  InterferometricStackType,
  ProductImageStacType,
  SearchResultElementType,
  SearchResultsType
} from "../types/product";
import translation from "../translations";
import {
  getAllSelectableProductImagesFrom,
  getSortFieldsFrom,
  INTERFEROMETRIC_STACK_DETAILS_ORDER_BY_FIELDS,
  isProductImageSearchResultElement
} from "./searchResultsHelper";
import {fromISOStringToUTCMoment} from "./dateHelper";
import {changeProductImageGeometryWith} from "./interferometricStackHelper";
import {getIntersectionShapeFeatureBetweenGeoJSON, isAPolygon} from "./geoJSONHelper";
import {CartOrderType} from "../types/cart";
import translations from '../translations';

interface productCategoryNameMappingType {
  productCategory: {
    code: string,
    name: string
  },
  productName: {
    code: string,
    name: string,
    mission_type: string
  }
}

export const productCategoryNameMappingByConfigurations = (
  configurations: ConfigurationsType,
  allProduct
): productCategoryNameMappingType => {
  const mapping:productCategoryNameMappingType = {
    productCategory: {
      code: configurations.productCategory,
      name: "",
    },
    productName: {
      code: configurations.productName,
      name: "",
      mission_type: "",
    }
  };

  const productCategory = allProduct.find((item) => item.code === `${configurations.productCategory}`);
  if (productCategory && productCategory.products) {
    mapping.productCategory.name = productCategory.tab_name;

    const productName = productCategory.products.find((item) => item.code === `${configurations.productName}`);
    if (productName) {
      mapping.productName.name = productName.name;
      mapping.productName.mission_type = productName.mission_type;
    }
  }

  return mapping;
};

export const getDefaultProductImageURLBy = (productImage: ProductImageStacType) => {
  const missionDescription = (productImage.properties && productImage.properties.mission) || "";
  let imageUrl = "http://placehold.it/128x128&text=No%20Image";
  switch (missionDescription) {
    case "COSMO-SkyMed (I Generation)":
      imageUrl = require("../images/csk.jpg");
      break;
    case "COSMO-SkyMed (II Generation)":
      imageUrl = require("../images/csg.png");
      break;
    default:
      break
  }
  return imageUrl;
};

export const labelling = (
  value,
  parseValue = ((v) => v)
): string => {
  return hasBeenFilled(value)
    ? `${parseValue(value)}`
    : "N.A.";
};

export const percentageLabelling = (
  value
): string => {
  return labelling(value, (v) => {
    const numberValue = isInteger(v)
      ? v
      : _.ceil(v, 1);
    return `${numberValue}%`;
  });
};

export const isInteger = (value: number) => {
  return value % 1 === 0;
};

export const getProductIdentifierFrom = (
  productImage: ProductImageStacType
): string => {
  const product_identifier = (productImage.properties && productImage.properties.product_identifier) || "";
  const product_type = (productImage.properties && productImage.properties.product_type) || "";
  return productImage.id || `${product_identifier}${product_type}`;
};

export const getProductIdentifiersFrom = (
  productImages: ProductImageStacType[]
): string[] => {
  return productImages.map((productImage) => getProductIdentifierFrom(productImage));
};

export const isSameProductImageOf = (
  productImage: ProductImageStacType
) => {
  return (otherProductImage: ProductImageStacType) => {
    return getProductIdentifierFrom(productImage) === getProductIdentifierFrom(otherProductImage)
  }
};

export const areAllSearchResultsCheckboxesSelected = (
  configurations: ConfigurationsType,
  ordersSubmitted: CartOrderType[],
  searchResults: SearchResultsType,
  selectedProducts: ProductImageStacType[]
): boolean => {
  let arePresent = false;
  const productImages: ProductImageStacType[] = getAllSelectableProductImagesFrom(configurations, ordersSubmitted, searchResults);
  if (productImages.length > 0) {
    const productImagesContainerIDs = getProductIdentifiersFrom(selectedProducts);
    arePresent = productImages.every((item) => productImagesContainerIDs.includes(getProductIdentifierFrom(item)));
  }
  return arePresent;
};

export const getProductImagesDifference = (
  firstProductImages: ProductImageStacType[],
  secondProductImages: ProductImageStacType[]
): ProductImageStacType[] => {
  const secondProductImagesIds = getProductIdentifiersFrom(secondProductImages);
  return firstProductImages.filter((p) => {
    const id = getProductIdentifierFrom(p);
    return !secondProductImagesIds.includes(id);
  });
};

export interface SearchResultDataPropertyType {
  label: string,
  value: any,
  html?: any,
  extra?: any
}

export const getProductImageSearchResultColumns = (
  configurations: ConfigurationsType,
  productImage: ProductImageStacType,
  language: string
): SearchResultDataPropertyType[] => (
  getSearchResultsColumns(productImage.properties, getSortFieldsFrom(configurations, false, language), language)
);

export const getInterferometricStackDetailsResultColumns = (
  configurations: ConfigurationsType,
  productImage: ProductImageStacType,
  language: string
): SearchResultDataPropertyType[] => (
  getSearchResultsColumns(productImage.properties, INTERFEROMETRIC_STACK_DETAILS_ORDER_BY_FIELDS, language)
);

export const getInterferometricStackSearchResultColumns = (
  configurations: ConfigurationsType,
  stack: InterferometricStackType,
  language: string
): SearchResultDataPropertyType[] => (
  getSearchResultsColumns(stack, getSortFieldsFrom(configurations, true, language), language)
);

const getSearchResultsColumns = (
  searchResultProperties: any,
  sortFields: any,
  language: string
): SearchResultDataPropertyType[] => {
  return sortFields
    .map((sortField, index) => {
      const fieldValue = searchResultProperties && searchResultProperties[sortField.name];

      const property: SearchResultDataPropertyType = {
        label: translations(sortField.description, language),
        value: ["cloud_cover"].includes(sortField.name)
          ? percentageLabelling(fieldValue)
          : labelling(fieldValue)
      };

      if (sortField.type === "date") {
        property.html = (
          <>
            {moment.utc(fieldValue).format("YYYY/MM/DD")}
            <br />
            {moment.utc(fieldValue).format("HH:mm:ss")}
          </>
        )
      }

      // data enrichment
      if (index === 0 && (
      // if (sortField.name === "mission" && (
          searchResultProperties.hasOwnProperty('ownership') && searchResultProperties['ownership'] !== 'public'
      )) {
        property.extra = (
            <span className="delayed">
              {translations("delayed", language)}
            </span>
        )
      }

      return property;
    });
};

const getProductImageDetailsProperties = (
  configurations: ConfigurationsType,
  productImageDetails: ProductImageStacType,
  language: string
) => {
  const properties = productImageDetails.properties || {};
  let propertyKeys = ["mission", "start_datetime", "orbit_direction", "acquisition_mode", "product_type", "platform"]
  let otherProperties = Object.keys(properties) || [];
  if (otherProperties && otherProperties.length > 0) {
    otherProperties = otherProperties.filter((p) => !(propertyKeys.concat(["gmlfootprint"])).includes(p));
    otherProperties = otherProperties.sort((a, b) => {
      const labelTextA = translation(a, language) || a;
      const labelTextB = translation(b, language) || b;
      if (labelTextA && labelTextB) {
        return labelTextA.localeCompare(labelTextB)
      }
      return 0
    });
  }

  propertyKeys = propertyKeys.concat(otherProperties);

  return propertyKeys.reduce((acc, key) => ({
    ...acc,
    [key]: properties[key]
  }), {})
};

const getInterferometricStackDetailsProperties = (
  configurations: ConfigurationsType,
  interferometricStackDetails: InterferometricStackType
) => {
  const {
    mission, // mission description
    acquisition_mode,
    orbit_direction,
    identifier,
  } = interferometricStackDetails;

  const lookSide = interferometricStackDetails.items && interferometricStackDetails.items[0] &&
    interferometricStackDetails.items[0].properties && interferometricStackDetails.items[0].properties['lookside'];

   return {
     "Mission": mission,
     "Acquisition Mode": acquisition_mode,
     "Pass Direction": orbit_direction,
     "Stack Identifier": identifier,
     "Look Side": lookSide,
   }
};

export const getSearchResultDetailsPropertiesColumns = (
  configurations: ConfigurationsType,
  searchResultDetails: SearchResultElementType,
  language: string
) => {
  const properties = isProductImageSearchResultElement(searchResultDetails)
    ? getProductImageDetailsProperties(configurations, searchResultDetails as ProductImageStacType, language)
    : getInterferometricStackDetailsProperties(configurations, searchResultDetails as InterferometricStackType);

  return Object.keys(properties)
    .filter((key) => hasBeenFilled(properties[key]))
    .map((key) => {
      const value = properties[key];
      let textValue;
      if (key.match(/date/i)) {
        textValue = moment.utc(value).format("YYYY/MM/DD HH:mm:ss");
      } else if (key.match(/cloud_cover/i)) {
        textValue = percentageLabelling(value);
      } else {
        textValue = labelling(value);
      }

      return {
        label: translation(key, language) || key,
        textValue,
        value
      }
    });
};

export const findIndexOfProductImageFrom = (
  productImages: ProductImageStacType[],
  productImage: ProductImageStacType
): number => {
  return productImages.findIndex((p) => (
    getProductIdentifierFrom(p) === getProductIdentifierFrom(productImage)
  ));
};

export const sortProductImagesByAcquisitionDateStartDESC = (
  productImages: ProductImageStacType[]
): ProductImageStacType[] => (
  productImages.sort((item, nextItem) => {
    const id1 = getProductIdentifierFrom(item);
    const id2 = getProductIdentifierFrom(nextItem);
    const acquisitionDateStart1 = item.properties && item.properties['start_datetime'];
    const acquisitionDateStart2 = nextItem.properties && nextItem.properties['start_datetime'];
    const momentDate1 = fromISOStringToUTCMoment(acquisitionDateStart1);
    const momentDate2 = fromISOStringToUTCMoment(acquisitionDateStart2);
    let isSort = id1 < id2 ? 1 : -1;
    if (momentDate1 && momentDate2) {
      if (momentDate1.isBefore(momentDate2)) {
        isSort = 1;
      } else if (momentDate2.isBefore(momentDate1)) {
        isSort = -1;
      }
    }

    return isSort;
  })
)
export const intersectAllProductImages = (
  productImages: (ProductImageStacType | null)[]
): ProductImageStacType | null => (
  productImages.length > 0
    ? productImages.reduce((acc: ProductImageStacType | null, nextProductImage: ProductImageStacType | null) => {
      let onDem: any = null;
      if (acc && nextProductImage && isAPolygon(acc) && isAPolygon(nextProductImage)) {
        const intersectionShape = getIntersectionShapeFeatureBetweenGeoJSON(acc, nextProductImage);
        onDem = intersectionShape && isAPolygon(intersectionShape)
          ? changeProductImageGeometryWith(acc, intersectionShape)
          : null
      }
      return onDem;
    }, productImages[0])
    : null
);
